'use client';

import { useIsomorphicLayoutEffect } from '@repo/common-utils';
import { createContext, ReactNode, useCallback, useContext, useRef } from 'react';

import { PageNavigationEventAction, PageNavigationEventContextType } from '../types';

export const PageNavigationEventContext = createContext<PageNavigationEventContextType>({
  registerNavigationEvent: () => {},
  removeNavigationEvent: () => {},
  getPreviousServicePage: () => null
});

export const PageNavigationEventProvider = ({ children, serviceId: _serviceId }: { children: ReactNode; serviceId?: string }) => {
  const actionsRef = useRef<Set<PageNavigationEventAction>>(new Set());
  const previousSameDocumentUrlRef = useRef<string | null>(null);

  const executeRegisteredActions = useCallback(() => {
    actionsRef.current.forEach((action) => {
      action();
    });
  }, []);

  const registerNavigationEvent = useCallback((action: PageNavigationEventAction) => {
    actionsRef.current.add(action);
  }, []);

  const removeNavigationEvent = useCallback((action: PageNavigationEventAction) => {
    actionsRef.current.delete(action);
  }, []);

  const getPreviousServicePage = useCallback(() => {
    if (typeof window === 'undefined') return null;

    if (!window.navigation) {
      const previousUrl = previousSameDocumentUrlRef.current;
      if (!previousUrl) return { isExist: false, page: null };

      return {
        isExist: true,
        page: {
          url: previousUrl,
          sameDocument: true
        }
      };
    }

    const navigation = window.navigation;
    if (!navigation.currentEntry) return { isExist: false, page: null };

    const current = navigation.currentEntry;
    const previous = navigation.entries()[current.index - 1];

    if (!previous) return { isExist: false, page: null };

    return { isExist: new URL(previous.url, window.location.href).origin === window.location.origin, page: previous };
  }, []);

  useIsomorphicLayoutEffect(() => {
    const capturedPushState = window.history.pushState;
    const capturedReplaceState = window.history.replaceState;

    const wrappedPushState: History['pushState'] = function (data, unused, url) {
      const previousUrl = window.location.href;
      capturedPushState.apply(window.history, [data, unused, url]);
      previousSameDocumentUrlRef.current = previousUrl;
      executeRegisteredActions();
    };

    const wrappedReplaceState: History['replaceState'] = function (data, unused, url) {
      capturedReplaceState.apply(window.history, [data, unused, url]);
      executeRegisteredActions();
    };

    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) executeRegisteredActions();
    };

    window.history.pushState = wrappedPushState;
    window.history.replaceState = wrappedReplaceState;
    window.addEventListener('popstate', executeRegisteredActions);
    window.addEventListener('pageshow', handlePageShow);
    window.addEventListener('hashchange', executeRegisteredActions);

    return () => {
      if (window.history.pushState === wrappedPushState) {
        window.history.pushState = capturedPushState;
      }
      if (window.history.replaceState === wrappedReplaceState) {
        window.history.replaceState = capturedReplaceState;
      }
      window.removeEventListener('popstate', executeRegisteredActions);
      window.removeEventListener('pageshow', handlePageShow);
      window.removeEventListener('hashchange', executeRegisteredActions);
    };
  }, [executeRegisteredActions]);

  return (
    <PageNavigationEventContext.Provider value={{ registerNavigationEvent, removeNavigationEvent, getPreviousServicePage }}>
      {children}
    </PageNavigationEventContext.Provider>
  );
};

export const usePageNavigationEvent = () => {
  return useContext(PageNavigationEventContext);
};
